/**
 * This file may contain code that uses generative AI for code assistance, unit testing and/or entire functions.
 * The generative model(s) used may be a combination of GitHub Copilot, OpenAI ChatGPT or others.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import {
  getCommandIntelligenceUserIdentityKey,
  resolveCommandIntelligenceDrawerPrefsCacheKey,
} from './buildCommandIntelligenceWidthCacheKey';
import { useEntityContext } from '../../util/entity-provider/context';
import { hasActiveSkuRef } from '../../util/hasActiveSkuRef';
import { AmbientCommandIntelligenceProvider, CommandIntelligenceGlobalRegistrar } from './ambient-context';
import { resolveEffectiveRegistration } from './ambient-context/resolve-effective-registration';
import CommandIntelligenceDrawer from './CommandIntelligenceDrawer.jsx';
import { CommandIntelligenceContext } from './CommandIntelligenceContext';
import { COMMAND_CLOUD_AI_SKU_REF } from './constants';
import { REGISTRATION_KIND_BASE, REGISTRATION_KIND_OVERLAY } from './ambient-context/constants';
import { useCommandIntelligencePersistedOpenState } from './useCommandIntelligencePersistedOpenState';
import { useCommandIntelligenceSession } from './useCommandIntelligenceSession';
import { useUserContext } from '../user';

const CommandIntelligenceProvider = ({ children, commandIntelligence, appId }) => {
  const [baseRegistration, setBaseRegistration] = useState(null);
  const [overlayRegistration, setOverlayRegistration] = useState(null);
  const lastRegistrationRef = useRef(null);
  const { entitlements, entityRef } = useEntityContext();
  const { user } = useUserContext();

  const registration = resolveEffectiveRegistration({ baseRegistration, overlayRegistration });

  useEffect(() => {
    if (registration) {
      lastRegistrationRef.current = registration;
    }
  }, [registration]);

  const activeRegistration = registration ?? lastRegistrationRef.current;

  const userIdentityKey = useMemo(() => getCommandIntelligenceUserIdentityKey(user), [user]);
  const isDrawerPrefsContextReady = Boolean(entityRef) && Boolean(user);

  const drawerPrefsCacheKey = useMemo(() => {
    return resolveCommandIntelligenceDrawerPrefsCacheKey({
      entityRef,
      userIdentityKey,
    });
  }, [entityRef, userIdentityKey]);

  const getItemKey = activeRegistration?.getItemKey;
  const maxAttachedItems = activeRegistration?.maxAttachedItems;

  const {
    session,
    derived,
    openBlank,
    openForItem,
    openForItemList,
    updateAvailableItems,
    attachItems,
    detachItem,
    clearAttached,
    clearContext,
    close,
    reset,
  } = useCommandIntelligenceSession({ getItemKey, maxAttachedItems });

  const isEntitled = useMemo(() => {
    return hasActiveSkuRef(entitlements, COMMAND_CLOUD_AI_SKU_REF);
  }, [entitlements]);

  const ambientEnabled = Boolean(commandIntelligence?.ambientContext) && isEntitled;

  const enabled = Boolean(registration?.commandIntelligenceConfig) && registration?.enabled !== false && isEntitled;

  const canPersistDrawerPrefs = enabled && isDrawerPrefsContextReady;

  const shouldRenderDrawer = enabled && isDrawerPrefsContextReady;

  const { persistClosed } = useCommandIntelligencePersistedOpenState({
    enabled: canPersistDrawerPrefs,
    isEntitled,
    isContextReady: isDrawerPrefsContextReady,
    drawerPrefsCacheKey,
    sessionOpen: Boolean(session?.open),
    openBlank,
    clearContext,
    close,
  });

  const closeAndPersist = useCallback(() => {
    persistClosed();
    close();
  }, [close, persistClosed]);

  const openBlankGated = useCallback(
    (...args) => {
      if (!enabled) {
        return;
      }

      openBlank(...args);
    },
    [enabled, openBlank]
  );

  const openForItemGated = useCallback(
    (...args) => {
      if (!enabled) {
        return;
      }

      openForItem(...args);
    },
    [enabled, openForItem]
  );

  const openForItemListGated = useCallback(
    (...args) => {
      if (!enabled) {
        return;
      }

      openForItemList(...args);
    },
    [enabled, openForItemList]
  );

  useEffect(() => {
    if (!isEntitled && session?.open) {
      persistClosed();
      close();
      reset();
    }
  }, [isEntitled, session?.open, close, persistClosed, reset]);

  const configure = useCallback(options => {
    if (!options?.commandIntelligenceConfig) {
      return;
    }

    const nextRegistration = {
      commandIntelligenceConfig: options.commandIntelligenceConfig,
      renderContextBar: options.renderContextBar,
      getItemKey: options.getItemKey,
      maxAttachedItems: options.maxAttachedItems,
      prefsScopeId: options.prefsScopeId,
      widthCacheKey: options.widthCacheKey,
      enabled: options.enabled ?? true,
    };

    if (options.registrationKind === REGISTRATION_KIND_BASE) {
      setBaseRegistration(nextRegistration);

      return;
    }

    setOverlayRegistration(nextRegistration);
  }, []);

  const clearConfiguration = useCallback(
    ({ registrationKind = REGISTRATION_KIND_OVERLAY } = {}) => {
      if (registrationKind === REGISTRATION_KIND_BASE) {
        setBaseRegistration(null);

        return;
      }

      setOverlayRegistration(null);
      clearContext();
    },
    [clearContext]
  );

  const value = useMemo(
    () => ({
      enabled,
      session,
      derived,
      ambientEnabled,
      openBlank: openBlankGated,
      openForItem: openForItemGated,
      openForItemList: openForItemListGated,
      updateAvailableItems,
      attachItems,
      detachItem,
      clearAttached,
      close: closeAndPersist,
      configure,
      clearConfiguration,
    }),
    [
      enabled,
      session,
      derived,
      ambientEnabled,
      openBlankGated,
      openForItemGated,
      openForItemListGated,
      updateAvailableItems,
      attachItems,
      detachItem,
      clearAttached,
      closeAndPersist,
      configure,
      clearConfiguration,
    ]
  );

  const providerBody = (
    <CommandIntelligenceContext.Provider value={value}>
      {commandIntelligence?.agentRef ? (
        <CommandIntelligenceGlobalRegistrar
          agentRef={commandIntelligence.agentRef}
          enabled={ambientEnabled}
        />
      ) : null}
      {children}
      {shouldRenderDrawer ? (
        <CommandIntelligenceDrawer
          session={session}
          onClose={closeAndPersist}
          onAttachItems={attachItems}
          onDetachItem={detachItem}
          onClearAttached={clearAttached}
          commandIntelligenceConfig={activeRegistration.commandIntelligenceConfig}
          renderContextBar={activeRegistration.renderContextBar}
          widthCacheKey={drawerPrefsCacheKey}
        />
      ) : null}
    </CommandIntelligenceContext.Provider>
  );

  if (!ambientEnabled) {
    return providerBody;
  }

  return (
    <AmbientCommandIntelligenceProvider
      enabled={ambientEnabled}
      ambientContext={commandIntelligence?.ambientContext}
      appId={appId}
    >
      {providerBody}
    </AmbientCommandIntelligenceProvider>
  );
};

export default CommandIntelligenceProvider;
